import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { adminUpload } from '../api/apiClient';

async function getCroppedBlob(imageSrc, pixelCrop) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });

  const canvas = document.createElement('canvas');
  canvas.width  = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    const supportsWebP = canvas.toDataURL('image/webp').startsWith('data:image/webp');
    const mimeType = supportsWebP ? 'image/webp' : 'image/jpeg';
    canvas.toBlob((blob) => blob ? resolve({ blob, mimeType }) : reject(new Error('Canvas toBlob failed')), mimeType, 0.88);
  });
}

export function AdminImageCropper({ imageSrc, onClose, onUploaded }) {
  const [crop, setCrop]           = useState({ x: 0, y: 0 });
  const [zoom, setZoom]           = useState(1);
  const [croppedArea, setCroppedArea] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const onCropComplete = useCallback((_croppedAreaPercent, croppedAreaPixels) => {
    setCroppedArea(croppedAreaPixels);
  }, []);

  const handleApply = async () => {
    if (!croppedArea) return;
    setUploading(true);
    setUploadError(null);
    try {
      const { blob, mimeType } = await getCroppedBlob(imageSrc, croppedArea);
      const ext = mimeType === 'image/webp' ? 'webp' : 'jpg';
      const formData = new FormData();
      formData.append('image', blob, `cover.${ext}`);
      const result = await adminUpload('/admin/upload-image.php', formData);
      onUploaded(result.url);
      onClose();
    } catch (err) {
      setUploadError('Błąd przesyłania. Spróbuj ponownie.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="admin-cropper-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="admin-cropper-modal">
        <div className="admin-cropper-modal__header">
          <h3>Przytnij obraz</h3>
          <button className="admin-cropper-modal__close" onClick={onClose} aria-label="Zamknij">&times;</button>
        </div>

        <div className="admin-cropper-modal__crop-area">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={16 / 9}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="admin-cropper-modal__zoom-row">
          <label htmlFor="ac-zoom">Zoom</label>
          <input
            id="ac-zoom"
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </div>

        {uploadError && (
          <div style={{ padding: '8px 18px', color: '#bd0c0c', fontSize: 12 }}>{uploadError}</div>
        )}

        <div className="admin-cropper-modal__footer">
          <button className="admin-btn admin-btn--secondary" onClick={onClose} disabled={uploading}>
            Anuluj
          </button>
          <button className="admin-btn admin-btn--primary" onClick={handleApply} disabled={uploading}>
            {uploading ? 'Przesyłanie…' : 'Zastosuj i prześlij'}
          </button>
        </div>
      </div>
    </div>
  );
}
